import { useKeyPress } from '@openeducation/oe-rn-hooks';
import { Side } from '@openeducation/oe-rn-shared-types';
import { styled } from '@openeducation/oe-rn-themes';
import { AnimatePresence, MotiView } from 'moti';
import React from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { useWindowDimensions } from 'react-native';

import { SideMenuContent } from './side-menu-content';
import { SideMenuOverlay } from './side-menu-overlay';

export interface SideMenuProps extends React.ComponentProps<typeof MotiView> {
  side?: Side;
  width?: number;
  visible?: boolean;
  showOverlay?: boolean;
  children?: React.ReactNode;
  closeOnEscapePress?: boolean;
  style?: StyleProp<ViewStyle>;
  backgroundOverlayOpacity?: number;
  onOverlayPress?: () => void;
}

export const SideMenu = ({
  side = 'left',
  width = 0,
  visible = true,
  showOverlay = true,
  children,
  closeOnEscapePress = true,
  style,
  backgroundOverlayOpacity = 0.5,
  onOverlayPress,
}: SideMenuProps) => {
  const { width: screenWidth } = useWindowDimensions();

  const escPress = useKeyPress('Escape');

  React.useEffect(() => {
    if (closeOnEscapePress && escPress) {
      onOverlayPress?.();
    }
  }, [closeOnEscapePress, escPress, onOverlayPress]);

  return (
    <AnimatePresence>
      {visible && (
        <Container
          side={side}
          showOverlay={showOverlay}
          containerWidth={screenWidth}
        >
          {showOverlay && (
            <SideMenuOverlay
              opacity={backgroundOverlayOpacity}
              onPress={onOverlayPress}
            />
          )}
          <SideMenuContent side={side} width={width} style={style}>
            {children}
          </SideMenuContent>
        </Container>
      )}
    </AnimatePresence>
  );
};

interface ContainerProps {
  showOverlay: boolean;
  containerWidth?: number;
  side: Side;
}

const Container = styled(MotiView)<ContainerProps>`
  position: absolute;
  z-index: 0;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  overflow-x: hidden;
  ${({ side }) =>
    side === 'left' ? 'align-items: flex-start' : 'align-items: flex-end'};
  ${({ showOverlay, containerWidth }) =>
    showOverlay ? 'left: 0px;' : `width: ${containerWidth}px`};
`;
